import type { AltarOferta, AltarTroca, Chefe, ChefeVariante, EventoTabela } from './evento';
import { BAU, FRAGMENTO, PREFIXO_CAPA, TOKEN_CAPA } from './evento';
import { ApiError } from './types';

/**
 * O Chefe do Mundo no mock (techspec-chefe-mundo §07): o documento padrão do servidor (ChefeDoc.Padrao), um saldo
 * de fragmentos, as trocas e um Balron ao vivo com `?mockchefe=1`.
 */
const VARIANTES: readonly ChefeVariante[] = [
  { id: 'rubro', nome: 'Balron Rubro', matiz: 2118, mapa: 'fire-dungeon', ativa: true, chancePct: 50, montarias: ['cavalo-cinza-rubro', 'ridgeback-rubro', 'dragao-pantano-rubro'] },
  { id: 'brasa', nome: 'Balron em Brasa', matiz: 1258, mapa: 'fire-dungeon', ativa: true, chancePct: 50, montarias: ['cavalo-baio-brasa', 'besouro-brasa', 'hiryu-brasa'] },
  { id: 'glacial', nome: 'Balron Glacial', matiz: 1266, mapa: 'ice-dungeon', ativa: true, chancePct: 50, montarias: ['unicornio-glacial', 'dragao-blindado-glacial', 'cu-sidhe-glacial'] },
  { id: 'geada', nome: 'Balron da Geada', matiz: 1151, mapa: 'ice-dungeon', ativa: true, chancePct: 50, montarias: ['ostard-floresta-geada', 'urso-polar-geada', 'esqueleto-geada'] }
];

const PRECO: Readonly<Record<string, number>> = {
  'cavalo-cinza': 120, ridgeback: 150, 'dragao-pantano': 200, 'cavalo-baio': 120, besouro: 150, hiryu: 220,
  unicornio: 200, 'dragao-blindado': 220, 'cu-sidhe': 220, 'ostard-floresta': 120, 'urso-polar': 150, esqueleto: 150
};

function tabela(id: string, nome: string, comum: string[], raro: string[], epico: string[], lendaria: string): EventoTabela {
  const itens = (defs: string[], chance: number) => defs.map(def => ({
    def, qtdMin: def === FRAGMENTO ? (chance > 10 ? 10 : 25) : 1, qtdMax: def === FRAGMENTO ? (chance > 10 ? 10 : 25) : 1,
    chance: Math.round(chance / defs.length * 100) / 100
  }));
  return {
    id, nome, garantiaEventos: 10, garantiaRaridade: 'epico',
    raridades: [
      { id: 'comum', chance: 70, itens: itens(comum, 70) },
      { id: 'raro', chance: 22, itens: itens(raro, 22) },
      { id: 'epico', chance: 6.5, itens: itens(epico, 6.5) },
      { id: 'lendario', chance: 1.5, itens: [{ def: TOKEN_CAPA, qtdMin: 1, qtdMax: 1, chance: 1 }, { def: lendaria, qtdMin: 1, qtdMax: 1, chance: 0.5 }] }
    ]
  };
}

export function criarMockChefe(mockParam: (k: string) => string | null) {
  let saldo = Number(mockParam('mockfrag') ?? 180) || 0;
  const usadas = new Map<string, number>();
  const capas = new Set<string>();
  const vivo = mockParam('mockchefe') === '1';
  const inicio = Date.now();

  const ofertas = (): AltarOferta[] => {
    const lista: AltarOferta[] = [];
    for (const v of VARIANTES) {
      const capa = `${PREFIXO_CAPA}${v.id}`;
      lista.push(oferta(`capa-${v.id}`, capa, `Capa do ${v.nome}`, 1, 250, v.id, true, 'total', 1, 'semana', 3));
      for (const m of v.montarias) {
        const bicho = m.slice(0, m.length - v.id.length - 1);
        const p = PRECO[bicho] ?? 150;
        lista.push(oferta(m, m, '', 1, p, v.id, false, 'total', 1, 'semana', p >= 200 ? 3 : 5));
      }
    }
    lista.push(oferta('bau-balron', BAU, 'Baú do Balron', 1, 20, '', false, 'semana', 3, 'nenhum', 0));
    lista.push(oferta('valorite-5', 'valorite-ingot', 'Valorite Ingot', 5, 15, '', false, 'nenhum', 0, 'nenhum', 0));
    return lista;
  };

  function oferta(id: string, item: string, nome: string, qtd: number, preco: number, variante: string, destaque: boolean,
    limitePeriodo: string, limiteQtd: number, estoquePeriodo: string, estoqueQtd: number): AltarOferta {
    const u = usadas.get(id) ?? 0;
    const restam = estoquePeriodo === 'nenhum' ? -1 : Math.max(0, estoqueQtd - (id.startsWith('capa') ? 1 : 0) - u);
    const bloqueio = item.startsWith(PREFIXO_CAPA) && capas.has(item) ? 'jaTem'
      : limitePeriodo !== 'nenhum' && u >= limiteQtd ? 'limite'
        : restam === 0 ? 'esgotado' : null;
    return {
      id, item, nome, qtd, preco, variante, destaque, limitePeriodo, limiteQtd, usadas: u, estoquePeriodo, estoqueQtd,
      restam, deMs: null, ateMs: null, bloqueio
    };
  }

  const proximos = () => {
    const hoje = new Date();
    const as = (h: number, dia = 0) => new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + dia, h, 0, 0).getTime();
    return [as(13), as(19), as(13, 1), as(19, 1), as(13, 2)].filter(ms => ms > Date.now()).slice(0, 4)
      .map(ms => ({ id: new Date(ms).getHours() === 13 ? 'chefe-tarde' : 'chefe-noite', hora: new Date(ms).getHours() === 13 ? '13:00' : '19:00', emMs: ms, tipo: 'chefe-mundo' }));
  };

  return {
    proximos,
    capas,
    async ler(): Promise<Chefe> {
      const passou = (Date.now() - inicio) / 1000;
      const permil = Math.max(1, Math.round(1000 - passou * 2));
      return {
        aoVivo: vivo ? {
          id: 'mock', estado: 0, variante: 'glacial', nome: 'Balron Glacial', matiz: 1266, mapa: 'ice-dungeon',
          permil, fimEmMs: inicio + 1_200_000, lutadores: 14,
          ranking: [
            { lugar: 1, nome: 'Lady Morgana', dano: 9_412, pct: 21.4 }, { lugar: 2, nome: 'Grimbold', dano: 7_980, pct: 18.1 },
            { lugar: 3, nome: 'Você', dano: 6_204, pct: 14.1 }
          ],
          meuLugar: 3, meuDano: 6_204, danoMinimo: 240, vidaMax: 48_000
        } : null,
        proximos: proximos(),
        anunciado: null,
        regras: {
          duracaoSegundos: 1200, avisoSegundos: 900, vidaBase: 30000, vidaPorJogador: 3000, ajusteSegundos: 120,
          chuvaSegundos: 30, chuvaAlcance: 8, chuvaPct: 15, invocaFases: [75, 40], invocaQtd: 2, furiaPct: 20, furiaGolpePct: 30,
          danoMinimoPct: 0.5, prataMin: 2000, prataMax: 6000, fragmentos: 3, fragmentosGolpeFinal: 1, fragmentosTop3: 2,
          capaBonus: 5, capaRepetidaFragmentos: 15
        },
        variantes: VARIANTES,
        mapas: [
          { mapa: 'fire-dungeon', ativo: true, tabela: tabela('chefe-fire', 'Chefe · Fire Dungeon',
            ['verite-ingot', 'valorite-ingot', 'bloodwood-log', 'heartwood-log', 'greater-healing'],
            ['tinta-prime-sangue', 'tinta-prime-carmim', FRAGMENTO], ['tinta-prime-ouro', 'tinta-prime-negro', FRAGMENTO], 'tinta-prime-fogo') },
          { mapa: 'ice-dungeon', ativo: true, tabela: tabela('chefe-ice', 'Chefe · Ice Dungeon',
            ['verite-ingot', 'valorite-ingot', 'frostwood-log', 'yew-log', 'greater-healing'],
            ['tinta-prime-purpura', 'tinta-prime-veneno', FRAGMENTO], ['tinta-prime-neve', 'tinta-prime-sombra', FRAGMENTO], 'tinta-prime-gelo') }
        ],
        altar: {
          moeda: FRAGMENTO, saldo, ofertas: ofertas(),
          bau: [
            { raridade: 'comum', chance: 80, tintas: ['carvao', 'vinho', 'vermelho', 'mostarda', 'musgo', 'verde', 'anil', 'roxo'].map(c => `tinta-${c}`) },
            { raridade: 'raro', chance: 17, tintas: ['sangue', 'veneno', 'purpura', 'carmim'].map(c => `tinta-prime-${c}`) },
            { raridade: 'epico', chance: 2.5, tintas: ['neve', 'sombra', 'ouro', 'negro'].map(c => `tinta-prime-${c}`) },
            { raridade: 'lendario', chance: 0.5, tintas: ['fogo', 'gelo', 'arcano'].map(c => `tinta-prime-${c}`) }
          ]
        },
        capas: [...capas],
        garantiaContador: 4,
        versao: 3
      };
    },
    async trocar(id: string, versao: number): Promise<AltarTroca> {
      if (versao !== 3) throw new ApiError('O Altar mudou. Veja as ofertas de novo.', 409, 'chefe.altarMudou');
      const o = ofertas().find(x => x.id === id);
      if (!o) throw new ApiError('Essa oferta não está no Altar.', 404, 'chefe.oferta');
      if (o.bloqueio === 'jaTem') throw new ApiError('Você já tem esta capa no guarda-roupa.', 409, 'chefe.jaTemCapa');
      if (o.bloqueio === 'limite') throw new ApiError('Você já trocou o que podia nesta oferta.', 409, 'chefe.limite');
      if (o.bloqueio === 'esgotado') throw new ApiError('Esgotado no shard neste período.', 409, 'chefe.esgotado');
      if (saldo < o.preco) throw new ApiError('Você não tem fragmentos bastantes.', 409, 'chefe.saldo');
      saldo -= o.preco;
      usadas.set(id, (usadas.get(id) ?? 0) + 1);
      const capa = o.item.startsWith(PREFIXO_CAPA);
      if (capa) capas.add(o.item);
      return { oferta: id, entregue: o.item === BAU ? 'tinta-vermelho' : o.item, qtd: o.qtd, saldo, guardaRoupa: capa, raridade: o.item === BAU ? 'comum' : 'altar' };
    }
  };
}

/** A matiz de cada cor do mock, para o guarda-roupa. */
export const MATIZ_DA_CAPA: Readonly<Record<string, number>> = Object.fromEntries(VARIANTES.map(v => [`${PREFIXO_CAPA}${v.id}`, v.matiz]));
export const NOME_DA_CAPA: Readonly<Record<string, string>> = Object.fromEntries(VARIANTES.map(v => [`${PREFIXO_CAPA}${v.id}`, `Capa do ${v.nome}`]));
